import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Breadcrumbs,
  CircularProgress,
  Button,
  FormControl,
  Select,
  MenuItem,
  Stack,
  Alert,
  Pagination,
  TextField,
  InputLabel,
} from "@mui/material";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import RefreshIcon from "@mui/icons-material/Refresh";
import JobDetailModal from "./JobDetailModal";
import { formatDateTime, getStatusColor } from "./utils";
import { getIntegrationJobs } from "app/services/api/integrations";

const ProductSyncLogs = () => {
  const { t } = useTranslation();

  // Get customerId from localStorage (same pattern as widgets.js)
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const customerId = user?.customer?.customerId;

  // State for data
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  // Pagination state
  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(1);

  // Filter state
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Table sorting state
  const [orderBy, setOrderBy] = useState("createdAt");
  const [order, setOrder] = useState("desc");

  // Modal state
  const [selectedJob, setSelectedJob] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Fetch jobs from API
  const fetchJobs = async () => {
    if (!customerId) {
      setError(t("pages.logs.errors.noCustomerId"));
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Calculate offset from page
    const offset = (page - 1) * limit;

    // Build query params - convert dateFrom/dateTo to ISO strings if provided
    const queryParams = {
      status: statusFilter || undefined,
      dateFrom: dateFrom ? new Date(dateFrom).toISOString() : undefined,
      dateTo: dateTo ? new Date(dateTo + "T23:59:59").toISOString() : undefined,
      limit,
      offset,
    };

    try {
      const response = await getIntegrationJobs(customerId, queryParams);

      // Handle response - could be array directly or wrapped in data.items property
      let jobsData = [];
      let total = 0;

      if (Array.isArray(response)) {
        jobsData = response;
        total = response.length;
      } else if (Array.isArray(response?.data?.items)) {
        // Backend returns { result: true, data: { items: [...], total: N } }
        jobsData = response.data.items;
        total = response.data.total || jobsData.length;
      } else if (Array.isArray(response?.data)) {
        jobsData = response.data;
        total = jobsData.length;
      } else if (Array.isArray(response?.jobs)) {
        jobsData = response.jobs;
        total = response.total || jobsData.length;
      } else if (Array.isArray(response?.items)) {
        jobsData = response.items;
        total = response.total || jobsData.length;
      }

      setJobs(jobsData);
      setTotalCount(total);
    } catch (err) {
      console.error("Error fetching jobs:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          t("pages.logs.errors.fetchFailed"),
      );
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount and when filter/pagination changes
  useEffect(() => {
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, dateFrom, dateTo, page, limit, customerId]);

  // Sort jobs on frontend (backend should return newest first, but we ensure it)
  const sortedJobs = useMemo(() => {
    // Ensure jobs is always an array
    const jobsArray = Array.isArray(jobs) ? jobs : [];
    return [...jobsArray].sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];

      // Handle nested progress.message
      if (orderBy === "message") {
        aVal = a.progress?.message || "";
        bVal = b.progress?.message || "";
      }

      if (aVal < bVal) return order === "asc" ? -1 : 1;
      if (aVal > bVal) return order === "asc" ? 1 : -1;
      return 0;
    });
  }, [jobs, orderBy, order]);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleRowClick = (job) => {
    setSelectedJob(job);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedJob(null);
  };

  // Pagination handlers
  const handleLimitChange = (event) => {
    setLimit(event.target.value);
    setPage(1); // Reset page when limit changes
  };

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
  };

  // Filter handlers - reset page when any filter changes
  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(1);
  };

  const handleDateFromChange = (event) => {
    setDateFrom(event.target.value);
    setPage(1);
  };

  const handleDateToChange = (event) => {
    setDateTo(event.target.value);
    setPage(1);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setStatusFilter("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  // Calculate total pages
  const totalPages = Math.ceil(totalCount / limit);

  // Calculate duration in minutes and seconds
  const formatDuration = (job) => {
    const startTime = job.createdAt;
    const endTime = job.progress?.finishedAt || job.updatedAt;

    if (!startTime || !endTime) return "—";

    try {
      const start = new Date(startTime).getTime();
      const end = new Date(endTime).getTime();

      if (isNaN(start) || isNaN(end)) return "—";

      const durationMs = end - start;
      if (durationMs < 0) return "—";

      const totalSeconds = Math.floor(durationMs / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;

      return `${minutes}:${seconds.toString().padStart(2, "0")}`;
    } catch {
      return "—";
    }
  };

  const columns = [
    { id: "status", label: t("pages.logs.table.status"), sortable: true },
    { id: "createdAt", label: t("pages.logs.table.createdAt"), sortable: true },
    { id: "duration", label: t("pages.logs.table.duration"), sortable: false },
    {
      id: "integration",
      label: t("pages.logs.table.integration"),
      sortable: true,
    },
    { id: "type", label: t("pages.logs.table.type"), sortable: true },
    { id: "step", label: t("pages.logs.table.step"), sortable: true },
    { id: "message", label: t("pages.logs.table.message"), sortable: false },
  ];

  const statusOptions = [
    { value: "", label: t("pages.logs.filters.all") },
    { value: "running", label: "running" },
    { value: "pending", label: "pending" },
    { value: "done", label: "done" },
    { value: "failed", label: "failed" },
    { value: "cancelled", label: "cancelled" },
  ];

  // Check if any filter is active
  const hasActiveFilters = statusFilter || dateFrom || dateTo;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs
          sx={{
            "& a": {
              color: "text.secondary",
              textDecoration: "none",
            },
            "& a:hover": {
              textDecoration: "underline",
            },
          }}
        >
          <Link to="/logs">{t("pages.logs.title")}</Link>
          <Typography color="text.primary">
            {t("pages.logs.productSync.title")}
          </Typography>
        </Breadcrumbs>
        <Typography variant="h2">
          {t("pages.logs.productSync.title")}
        </Typography>
      </Box>

      {/* Filters and Controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack spacing={2}>
          {/* First row - Filters */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
            flexWrap="wrap"
          >
            {/* Status Filter */}
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>{t("pages.logs.filters.status")}</InputLabel>
              <Select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                label={t("pages.logs.filters.status")}
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Date From */}
            <TextField
              size="small"
              type="date"
              label={t("pages.logs.filters.dateFrom")}
              value={dateFrom}
              onChange={handleDateFromChange}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />

            {/* Date To */}
            <TextField
              size="small"
              type="date"
              label={t("pages.logs.filters.dateTo")}
              value={dateTo}
              onChange={handleDateToChange}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />

            {/* Clear Filters Button */}
            {hasActiveFilters && (
              <Button
                variant="text"
                size="small"
                onClick={handleClearFilters}
                disabled={loading}
              >
                {t("pages.logs.filters.clear")}
              </Button>
            )}

            {/* Refresh Button */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={fetchJobs}
              disabled={loading}
            >
              {t("pages.logs.actions.refresh")}
            </Button>
          </Stack>

          {/* Second row - Pagination */}
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="flex-end"
          >
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <Select
                value={limit}
                onChange={handleLimitChange}
                disabled={loading}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>

            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              disabled={loading}
            />
          </Stack>
        </Stack>
      </Paper>

      {/* Error State */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={fetchJobs}>
              {t("pages.logs.actions.retry")}
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Table */}
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={column.id}>
                    {column.sortable ? (
                      <TableSortLabel
                        active={orderBy === column.id}
                        direction={orderBy === column.id ? order : "asc"}
                        onClick={() => handleSort(column.id)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    align="center"
                    sx={{ py: 4 }}
                  >
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : sortedJobs.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    align="center"
                    sx={{ py: 4 }}
                  >
                    <Typography color="text.secondary">
                      {t("pages.logs.noData")}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedJobs.map((job) => (
                  <TableRow
                    key={job._id}
                    hover
                    onClick={() => handleRowClick(job)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>
                      <Chip
                        label={job.status}
                        color={getStatusColor(job.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDateTime(job.createdAt)}</TableCell>
                    <TableCell>{formatDuration(job)}</TableCell>
                    <TableCell>{job.integration}</TableCell>
                    <TableCell>{job.type}</TableCell>
                    <TableCell>{job.step || "—"}</TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 300,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {job.progress?.message || "—"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Job Detail Modal */}
      <JobDetailModal
        open={modalOpen}
        onClose={handleCloseModal}
        job={selectedJob}
      />
    </Box>
  );
};

export default ProductSyncLogs;
